/**
 * Supabase Auth context for the SaaS SPA.
 *
 * Provides login, logout, and session state to the entire app.
 * Uses Supabase's OAuth flow (Google / GitHub).
 */

import {createClient, type SupabaseClient, type Session, type User} from '@supabase/supabase-js';
import {createContext, useContext, useEffect, useState, type ReactNode} from 'react';

// ---------------------------------------------------------------------------
// Supabase client singleton
// ---------------------------------------------------------------------------

let supabase: SupabaseClient | null = null;

function getSupabase(): SupabaseClient {
  if (supabase) return supabase;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
  }

  supabase = createClient(url, anonKey);
  return supabase;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithGithub: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<{error: string | null}>;
  signUpWithEmail: (email: string, password: string) => Promise<{error: string | null}>;
  signOut: () => Promise<void>;
  /** The raw Supabase client (for direct queries if needed). */
  supabase: SupabaseClient;
  /** JWT access token for API calls. */
  accessToken: string | null;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({children}: {children: ReactNode}) {
  const client = getSupabase();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    client.auth.getSession().then(({data: {session}}) => {
      setSession(session);
      setLoading(false);
    });

    // Listen for auth changes (login, logout, token refresh)
    const {
      data: {subscription},
    } = client.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    await client.auth.signInWithOAuth({
      provider: 'google',
      options: {redirectTo: window.location.origin},
    });
  };

  const signInWithGithub = async () => {
    await client.auth.signInWithOAuth({
      provider: 'github',
      options: {redirectTo: window.location.origin},
    });
  };

  const signInWithEmail = async (email: string, password: string) => {
    const {error} = await client.auth.signInWithPassword({email, password});
    return {error: error?.message ?? null};
  };

  const signUpWithEmail = async (email: string, password: string) => {
    const {error} = await client.auth.signUp({email, password});
    return {error: error?.message ?? null};
  };

  const signOut = async () => {
    await client.auth.signOut();
    setSession(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        session,
        loading,
        signInWithGoogle,
        signInWithGithub,
        signInWithEmail,
        signUpWithEmail,
        signOut,
        supabase: client,
        accessToken: session?.access_token ?? null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
